import { useEffect, useState } from 'react';
import { App, Button, Card, Collapse, Flex, Form, Result, theme, Typography } from 'antd';
import { VehicleStep } from './steps/VehicleStep';
import { TakerStep } from './steps/TakerStep';
import { ConfirmationStep } from './steps/ConfirmationStep';
import { StepControls } from './StepControls';
import { getStepCollapseItem } from './getStepCollapseItem';
import { QuotationLoadingScreen } from 'Pages/QuotationPage/components/QuotationLoadingScreen';
import { useLoading } from '@shared/hooks/useLoading';
import { useCreateVehicle } from '@features/vehicles/hooks/useVehicles';
import { useCreateOrUpdateClient } from '@features/clients/hooks/useClients';
import { useCreateQuotation } from '@features/quotations/hooks/useQuotations';
import { getErrorMessage } from '@shared/utils/errors/getErrorMessage';
import { SUCCESS_COLOR } from '@shared/constants/colors';
import { STEPS_CONFIG, CONFIRMATION_STEP_CONFIG, TAKER_STEP_CONFIG, VEHICLE_STEP_CONFIG } from 'Pages/QuotationPage/constants/stepsConfig';
import { useNavigate } from 'react-router-dom';

// TODO: Pendiente pasar las solicitudes a tanStack Query usando hooks y reemplazar Redux por Zustand para el manejo de estados
import { useDispatch, useSelector } from 'react-redux';
import { globalSedeSetAction } from 'Redux/globals/selectedSede/actions';
import { getQuotationPrice } from 'Pages/DataGatewayPage/routerForm/utils';
import { initializeExpectedPackagesResponsesAction, setPetitionAliveAction } from 'Redux/actions/comparationQuotationActions'
import { generateInsuranceQuotation } from 'Redux/actions/globalQuotation';
import { ROUTES } from '@/App/Router/routes';

const { Text, Title } = Typography;

export const CreateQuotationMain = ({ currentStep, onNext, onPrevious, isExternal }) => {
  const [externalQuotationCreated, setExternalQuotationCreated] = useState(false);

  const { token: themeToken } = theme.useToken();
  const { notification } = App.useApp();
  const generateQuotationsByInsuranceCarrierLoading = useLoading();

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const selectedBranch = useSelector((state) => state.globals.selected_sede); // Se recupera la sede seleccionada en la app desde redux
  const branches = useSelector((state) => state.initialData.sedes);
  const userBranchIds = useSelector((state) => state.initialData.user?.sedes);

  useEffect(() => {
    if (!isExternal || selectedBranch?.id || !branches?.length || !userBranchIds?.length) return;

    const storedId = localStorage.getItem('globals--selected-sede');
    let selected = branches.find((branch) => userBranchIds.includes(branch.id) && branch.id === storedId);
    if (!selected) {
      selected = branches.find((branch) => branch.id === userBranchIds[0]);
      if (selected) localStorage.setItem('globals--selected-sede', selected.id);
    }
    if (selected) dispatch(globalSedeSetAction(selected));
  }, [isExternal, selectedBranch, branches, userBranchIds, dispatch]);

  const [quotationContext, setQuotationContext] = useState({
    beneficiary: {},
    isBeneficiarySameAsTaker: true,
    circulationCityName: '',
  });

  const [vehicleForm] = Form.useForm();
  const [takerForm] = Form.useForm();
  const [beneficiaryForm] = Form.useForm();

  const createVehicleMutation = useCreateVehicle();
  const createOrUpdateClientMutation = useCreateOrUpdateClient();
  const createQuotationMutation = useCreateQuotation();

  const isLoading = createVehicleMutation.isPending || createOrUpdateClientMutation.isPending || createQuotationMutation.isPending || generateQuotationsByInsuranceCarrierLoading.isLoading;

  const updateQuotationContext = (newContext) => {
    setQuotationContext((prev) => ({ ...prev, ...newContext }));
  }

  // Funciones de validación de los formularios
  const validationsByStep = {
    [VEHICLE_STEP_CONFIG.key]: async () => {
      await vehicleForm.validateFields();
    },
    [TAKER_STEP_CONFIG.key]: async () => {
      if (quotationContext.isBeneficiarySameAsTaker) {
        await takerForm.validateFields();
        updateQuotationContext({ beneficiary: {} }); // Se limpia 'beneficiary' si es el mismo que el tomador
        return;
      }

      // Se validan los formularios del tomador y el beneficiario
      const [_, beneficiary] = await Promise.all([
        takerForm.validateFields(),
        beneficiaryForm.validateFields(),
      ]);
      updateQuotationContext({ beneficiary });
    },
  };

  const handleNext = async () => {
    const { key: stepKey } = STEPS_CONFIG[currentStep]; // Obtiene la key del step actual
    try {
      await validationsByStep[stepKey]?.(); // Ejecuta la validación del step correspondiente si existe
    } catch (error) {
      // Ant Design rechaza con un objeto de campos, no con un Error. Los mensajes ya quedan en el formulario.
      if (error?.errorFields) return;
      throw error;
    }
    onNext();

    // Si se está en el paso de confirmación, se ejecutan las mutaciones
    if (stepKey === CONFIRMATION_STEP_CONFIG.key) {
      let createdQuotation;

      // Se crea el vehículo, tomador y la cotización
      try {
        const savedClient = await createOrUpdateClientMutation.mutateAsync(takerForm.getFieldsValue(true)); // Se crea/actualiza el tomador de primero porque es más propenso a fallar por validaciones
        const createdVehicle = await createVehicleMutation.mutateAsync(vehicleForm.getFieldsValue(true));

        const externalQuotation = localStorage.getItem('external_session') === 'true'
        createdQuotation = await createQuotationMutation.mutateAsync({
          vehicleId: createdVehicle.id,
          takerId: savedClient.id,
          isBeneficiarySameAsTaker: quotationContext.isBeneficiarySameAsTaker,
          beneficiary: quotationContext.beneficiary,
          externalQuotation,
          branchId: selectedBranch?.id,
          ...(externalQuotation && { notificationLink: `${window.location.origin}${ROUTES.QUOTATIONS.UPDATE('{id}')}` }),
        });

        notification.success({
          message: <Text strong>Cotización creada</Text>,
          description: '¡La cotización se ha creado correctamente!',
        });
      } catch (error) {
        notification.error({
          message: <Text strong>Cotización no creada</Text>,
          description: getErrorMessage(error),
        });

        onPrevious();
        return;
      };

      // Si es una cotización externa no se generan las cotizaciones por aseguradora
      if (isExternal) {
        setExternalQuotationCreated(true);
        return;
      }

      // Se generan las cotizaciones por aseguradora
      generateQuotationsByInsuranceCarrierLoading.start();
      try {
        const price = await getQuotationPrice(false);
        const { insurance_carriers: quotedInsuranceCarriers } = await dispatch(generateInsuranceQuotation(createdQuotation.id, price));
        dispatch(initializeExpectedPackagesResponsesAction(quotedInsuranceCarriers));
        dispatch(setPetitionAliveAction(true));
        // localStorage.setItem('consulting', true);
        navigate(ROUTES.QUOTATIONS.PLANS.LIST(createdQuotation.id), { replace: true });
      } catch (error) {
        notification.error({
          message: 'No fue posible generar las cotizaciones por aseguradora',
          description: getErrorMessage(error),
        });
      } finally {
        generateQuotationsByInsuranceCarrierLoading.stop();
      };
    }
  };

  const handleCirculationCityChange = (_, option) => {
    const name = option?.record.name ?? '';
    updateQuotationContext({ circulationCityName: name });
  }

  const handleBeneficiarySameAsTakerChange = (value) => {
    updateQuotationContext({ isBeneficiarySameAsTaker: value });
  }

  const componentsByStep = {
    [VEHICLE_STEP_CONFIG.key]: (
      <VehicleStep
        form={vehicleForm}
        onCirculationCityChange={handleCirculationCityChange}
        cardProps={isExternal ? {
          bordered: false,
          style: { background: 'transparent', boxShadow: 'none' },
          styles: { body: { padding: 0 } },
        } : undefined}
      />
    ),
    [TAKER_STEP_CONFIG.key]: (
      <TakerStep
        form={takerForm}
        beneficiaryForm={beneficiaryForm}
        isBeneficiarySameAsTaker={quotationContext.isBeneficiarySameAsTaker}
        onBeneficiarySameAsTakerChange={handleBeneficiarySameAsTakerChange}
        isExternal={isExternal}
      />
    ),
    [CONFIRMATION_STEP_CONFIG.key]: (
      <ConfirmationStep
        vehicle={{
          ...vehicleForm.getFieldsValue(true),
          circulationCityName: quotationContext.circulationCityName,
        }}
        taker={takerForm.getFieldsValue(true)}
        isExternal={isExternal}
      />
    ),
  };

  const items = STEPS_CONFIG.map(({ key, icon, title }, step) => {
    const isLastStep = step === STEPS_CONFIG.length - 1;

    return getStepCollapseItem({
      step,
      currentStep,
      icon,
      title,
      primaryColor: isLastStep ? SUCCESS_COLOR : themeToken.colorPrimary, // El ultimo paso usa el color SUCCESS_COLOR
      children: componentsByStep[key],
    })
  });

  if (isExternal) {
    const step = STEPS_CONFIG[currentStep];

    return (
      <Flex vertical style={{ flex: 1 }}>
        <Flex vertical style={{ padding: 'clamp(24px, 5vw, 40px) clamp(20px, 5vw, 40px) 0' }}>
          <Text style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: themeToken.colorPrimary }}>
            {`Paso ${currentStep + 1} de ${STEPS_CONFIG.length}`}
          </Text>
          <Title level={3} style={{ margin: 0 }}>{step?.title}</Title>
        </Flex>
        <div style={{ padding: '28px clamp(20px, 5vw, 40px)' }}>
          {step && componentsByStep[step.key]}
        </div>
        {currentStep < items.length && (
          <Flex
            justify='space-between'
            align='center'
            wrap='wrap'
            gap={12}
            style={{ marginTop: 'auto', borderTop: '1px solid #e4e8f0', padding: '20px clamp(20px, 5vw, 40px)' }}
          >
            <Text style={{ color: '#4b5565' }}>
              <span style={{ color: '#c4291c' }}>*</span>
              {' '}
              Campos obligatorios
            </Text>
            <StepControls current={currentStep} total={items.length} onNext={handleNext} onPrevious={onPrevious} isExternal />
          </Flex>
        )}
        {externalQuotationCreated && (
          <Result
            status='success'
            title='¡Tu cotización se ha creado correctamente!'
            subTitle='Ya tenemos toda la información. Un asesor la revisará y se pondrá en contacto contigo muy pronto.'
            extra={(
              <Button shape='round' onClick={() => navigate(-1)}>
                Finalizar
              </Button>
            )}
          />
        )}
        <QuotationLoadingScreen loading={isLoading} isExternal />
      </Flex>
    );
  }

  return (<>
    <Card>
      <Collapse
        activeKey={currentStep}
        bordered={false}
        expandIcon={() => null} // Oculta el ícono de expandir/colapsar, la navegación se gestiona únicamente con StepControls
        collapsible='icon' // Al no renderizarse el ícono, se deshabilita también la interacción en el header (elimina el cursor pointer)
        items={items}
        style={{ background: themeToken.colorBgContainer }}
      />
      {currentStep < items.length && ( // Muestra los controles de navegación solo si no se han completado todos los pasos
        <StepControls current={currentStep} total={items.length} onNext={handleNext} onPrevious={onPrevious} isExternal={isExternal} />
      )}
    </Card>
    {isExternal && externalQuotationCreated && (
      <Result
        status='success'
        title='¡Tu cotización se ha creado correctamente!'
        subTitle='Ya tenemos toda la información. Un asesor la revisará y se pondrá en contacto contigo muy pronto.'
        extra={
          <Button shape='round' onClick={() => navigate(-1)}>
            Finalizar
          </Button>
        }
      />
    )}
    <QuotationLoadingScreen loading={isLoading} isExternal={isExternal} />
  </>);
};